import { AsyncPipe } from '@angular/common';
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { URLOpenListenerEvent } from '@capacitor/app';
import { IonApp, IonLoading, IonRouterOutlet } from '@ionic/angular';
import config from 'capacitor.config';
import { AuthService } from './common/auth/auth.service';
import { AppService } from './common/capacitor/app.service';
import { AppleAuthService } from './common/capacitor/apple-auth.service';
import { CapacitorService } from './common/capacitor/capacitor.service';
import { GoogleAuthService } from './common/capacitor/google-auth.service';
import { PlatformService } from './common/capacitor/platform.service';
import { CleanupService } from './common/cleanup/cleanup.service';
import { LoadingService } from './common/loading/loading.service';
import { LoggingService } from './common/logging/logging.service';
import { UniversalLinkService } from './common/universal-link/universal-link.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonLoading, IonApp, IonRouterOutlet, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  appService = inject(AppService);
  authService = inject(AuthService);
  appleAuthService = inject(AppleAuthService);
  capacitorService = inject(CapacitorService);
  cleanupService = inject(CleanupService);
  googleAuthService = inject(GoogleAuthService);
  logger = inject(LoggingService);
  platformService = inject(PlatformService);
  universalLinkService = inject(UniversalLinkService);

  loading$ = inject(LoadingService).loading$;

  constructor() {
    this.initializeUniversalLinking();
  }

  async ngOnInit() {
    await this.platformService.ready();
    await this.universalLinkService.handleLaunchUrl();
    await this.initializeAppleAuth();
    await this.initializeGoogleAuth();

    // Run cleanup of old files in the background
    this.cleanupService.cleanupOldFiles().catch((error) => {
      this.logger.error('Cleanup service failed:', error);
    });
  }

  private async initializeAppleAuth() {
    if (!this.capacitorService.ios) {
      return;
    }

    if (!config.plugins?.SocialLogin?.apple?.clientId) {
      throw new Error('Missing clientId in capacitor.config.ts');
    }

    await this.appleAuthService.initialize();
  }

  private async initializeUniversalLinking() {
    if (!this.capacitorService.mobile) {
      return;
    }

    return this.platformService.ready().then(() => {
      this.appService.addUrlOpenListener(
        async (event: URLOpenListenerEvent) => {
          this.logger.log('URL Open Event received (hot start):', event);

          if (!event.url) {
            this.logger.warn('URL Open Event received but no URL provided');
            return;
          }

          this.logger.log('Processing hot start URL:', event.url);
          await this.universalLinkService.handleUniversalLink(event.url);
        },
      );
    });
  }

  private async initializeGoogleAuth() {
    if (!this.capacitorService.mobile) {
      return;
    }

    if (!config.plugins?.SocialLogin?.google?.webClientId) {
      throw new Error('Missing webClientId in capacitor.config.ts');
    }

    if (!config.plugins?.SocialLogin?.google?.iOSClientId) {
      throw new Error('Missing iOSClientId in capacitor.config.ts');
    }

    await this.googleAuthService.initialize();
  }
}
